// Fuzzy matching of what people type against subject names and aliases (spec §2.1).
// Shared by the client (typeahead) and the Worker (resolving parse results). Pure.

export type Matchable = { slug: string; name: string; aliases: string[]; learnable?: boolean };
export type Match<T extends Matchable = Matchable> = { item: T; score: number; via: string };

const STOP = new Set(["learn", "learning", "to", "the", "a", "an", "of", "and", "how", "i", "want", "study", "basics", "basic", "intro", "introduction", "with", "for", "in", "on", "my", "me", "get", "good", "at", "into"]);

export function normalize(s: string): string {
  return s
    .toLowerCase()
    .replace(/[’']/g, "")
    .replace(/[^a-z0-9+#.\s-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function tokens(s: string): string[] {
  return normalize(s)
    .split(/[\s-]+/)
    .filter((t) => t && !STOP.has(t));
}

/** 0..1: exact 1, the query is a prefix .9, one contains the other as whole words .8, token overlap below that. */
export function scoreOne(query: string, candidate: string): number {
  const q = normalize(query), c = normalize(candidate);
  if (!q || !c) return 0;
  if (q === c) return 1;
  if (c.startsWith(q)) return 0.9; // typing "lin" → "linear algebra"
  const padQ = ` ${q} `, padC = ` ${c} `;
  if (padC.includes(padQ)) return 0.8; // "algebra" → "linear algebra"; whole words only, so "rag" is not in "paraglider"
  const qt = tokens(q), ct = tokens(c);
  if (!qt.length || !ct.length) return 0;
  // "learn the guitar" → "guitar" (covers every real word); "australian history" → "history" does not (half)
  if (padQ.includes(padC) && ct.length >= Math.ceil(qt.length * 0.6)) return 0.8;
  let hit = 0;
  for (const t of qt) if (ct.some((x) => x === t || (t.length > 3 && (x.startsWith(t) || t.startsWith(x))))) hit++;
  const overlap = hit / Math.max(qt.length, ct.length);
  return overlap >= 0.5 ? 0.5 + overlap * 0.25 : 0;
}

export function match<T extends Matchable>(query: string, items: T[], limit = 8): Match<T>[] {
  const out: Match<T>[] = [];
  for (const item of items) {
    let best = scoreOne(query, item.name), via = item.name;
    for (const a of item.aliases) {
      const s = scoreOne(query, a);
      if (s > best) {
        best = s;
        via = a;
      }
    }
    if (best > 0) out.push({ item, score: best, via });
  }
  out.sort((a, b) => b.score - a.score || Number(Boolean(b.item.learnable)) - Number(Boolean(a.item.learnable)) || a.item.name.length - b.item.name.length);
  return out.slice(0, limit);
}

export function wordCount(s: string): number {
  return normalize(s).split(" ").filter(Boolean).length;
}

export function slugify(name: string): string {
  return normalize(name)
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/[\s-]+/g, "-")
    .slice(0, 60);
}
